import "./case-study.scss";

import PageTitleBreadcrumb from "@frontend/js/Components/Shared/PageTitleBreadcrumb/PageTitleBreadcrumb";
import CaseStudiesCards from "@frontend/js/Components/Site/CaseStudiesCards/CaseStudiesCards";
import ShareActions from "@frontend/js/Components/Shared/ShareActions/ShareActions";
import { useSectionNavigation } from "@frontend/js/hooks/useSectionNavigation";
import ImageLayer from "@frontend/js/Components/Shared/ImageLayer/ImageLayer";
import ExternalArrowIcon from "@frontend/images/icons/ExternalArrowIcon";
import MetaHead from "@frontend/js/Components/Shared/MetaHead/MetaHead";
import SiteLayout from "@frontend/js/Layouts/SiteLayout";
import DefaultImage from "@frontend/images/DefaultImage";
import FileIcon from "@frontend/images/icons/FileIcon";
import { memo, useMemo, useState } from "react";
import useT from "@frontend/js/hooks/useT";
import useRoute from "@frontend/js/hooks/useRoute";
import { usePage } from "@inertiajs/react";

const CaseStudyContent = memo(function CaseStudyContent({ contentRef, html }) {
    return <div ref={contentRef} dangerouslySetInnerHTML={html} />;
});

export default function CaseStudy({
    page,
    resource,
    related,
    meta = {
        title: "",
        description: "",
    },
}) {
    const { t } = useT("site");
    const route = useRoute();
    const pageProps = usePage().props;
    const locale = pageProps.currentLocale;
    const pageTitle = page?.title?.[locale] || "";
    const pageHref = route("frontend.page", { page: page?.slug });
    const caseStudy = resource?.data;
    const relatedCards = related?.data || [];
    const files = Array.isArray(caseStudy?.files) ? caseStudy.files : [];

    const currentCaseStudyTitle = caseStudy?.title || "Titlul studiului";
    const caseStudyImage = caseStudy?.image || null;
    const [hasImageError, setHasImageError] = useState(false);

    const content = caseStudy?.content || "";
    const contentMarkup = useMemo(() => ({ __html: content }), [content]);

    const {
        sections,
        activeSection,
        contentContainerRef,
        handleNavigationClick,
    } = useSectionNavigation(content);

    return (
        <>
            <MetaHead meta={meta} />

            <SiteLayout>
                <div className="caseStudy page-shell">
                    <PageTitleBreadcrumb
                        title={caseStudy?.title}
                        variant="large"
                        parentTitle={pageTitle}
                        parentHref={pageHref}
                    />
                    <div className="caseStudy__container">
                        <div className="caseStudy__container__content">
                            <div className="caseStudy__container__content__image">
                                <ImageLayer>
                                    {caseStudyImage && !hasImageError ? (
                                        <img
                                            src={caseStudyImage}
                                            alt={currentCaseStudyTitle}
                                            loading="lazy"
                                            onError={() =>
                                                setHasImageError(true)
                                            }
                                        />
                                    ) : (
                                        <div
                                            className="caseStudy__container__content__imageFallback"
                                            aria-hidden="true"
                                        >
                                            <DefaultImage />
                                        </div>
                                    )}
                                </ImageLayer>
                            </div>
                            <CaseStudyContent
                                contentRef={contentContainerRef}
                                html={contentMarkup}
                            />
                            {files.length > 0 && (
                                <>
                                    <h2>{t("case-study-files-title")}</h2>
                                    <div className="caseStudy__container__content__files">
                                        {files.map((file) => (
                                            <a
                                                key={file.id}
                                                href={file.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="caseStudy__container__content__files__file"
                                            >
                                                <div className="caseStudy__container__content__files__file__icon">
                                                    <FileIcon />{" "}
                                                    {file.extension || ""}
                                                </div>
                                                <div className="caseStudy__container__content__files__file__name">
                                                    <div className="caseStudy__container__content__files__file__name__icon">
                                                        <ExternalArrowIcon color="var(--text-subtle)" />
                                                    </div>{" "}
                                                    {file.title}
                                                    {file.size
                                                        ? ` — ${file.size}`
                                                        : ""}
                                                </div>
                                                <div className="caseStudy__container__content__files__file__date">
                                                    {file.date}
                                                </div>
                                            </a>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="caseStudy__container__navigation">
                            <div className="caseStudy__container__navigation__list">
                                {sections.map((section) => (
                                    <button
                                        key={section.id}
                                        type="button"
                                        className={`caseStudy__container__navigation__list__link ${activeSection === section.id ? "is-active" : ""}`}
                                        aria-current={
                                            activeSection === section.id
                                                ? "true"
                                                : undefined
                                        }
                                        onClick={() =>
                                            handleNavigationClick(section.id)
                                        }
                                    >
                                        {section.title}
                                    </button>
                                ))}
                            </div>
                            {caseStudy?.preview_file && (
                                <a
                                    href={caseStudy.preview_file.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="caseStudy__container__navigation__preview"
                                >
                                    {t("case-study-preview-button")}{" "}
                                    <ExternalArrowIcon />
                                </a>
                            )}
                            <ShareActions
                                title={t("share-label")}
                                copyLabel={t("copy-link-label")}
                                channels={[
                                    {
                                        id: "facebook",
                                        label: t("facebook-label"),
                                    },
                                    {
                                        id: "twitter",
                                        label: t("twitter-label"),
                                    },
                                    {
                                        id: "linkedin",
                                        label: t("linkedin-label"),
                                    },
                                ]}
                            />
                        </div>
                    </div>

                    {relatedCards.length > 0 && (
                        <div className="caseStudy__related">
                            <div className="caseStudy__related__container">
                                <h2>{t("related-cases-title")}</h2>
                                <CaseStudiesCards
                                    cards={relatedCards}
                                    className="caseStudy__related__container__grid"
                                    cardModifier="white"
                                />
                            </div>
                        </div>
                    )}
                </div>
            </SiteLayout>
        </>
    );
}
